import { ILookupItem } from '../types';
import {
  businessContextId,
  defaultDashboardId,
  siteUserGroupId
} from '../common/utils/business-configuration';
import { getNonNullUser } from '../common-package/user';
import { getHeaders, getUrl } from '../common-package/url';
import { Guid } from '../common-package/guid';

interface IRawRole extends ILookupItem {
  assignedUserId: number;
}
export interface ISetupData {
  roles: ILookupItem[];
  departments: IDepartment[];
}

export interface IUser extends ILookupItem {
  firstName: string;
  lastName: string;
  position: string;
  parentId: number;
}

export interface IDepartment extends ILookupItem {
  parentId?: number;
}
export interface IPushRole {
  name: string;
}

export interface IPushUser {
  firstName: string;
  lastName: string;
  department: number;
  role: number;
  email: string;
  password: string;
  emailNotification: number[];
  username: string;
}

const pullRoles = async () => {
  const { token } = getNonNullUser();
  const url = getUrl(`/UserManager/Role`, businessContextId);
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(token)
    });
    if (!response.ok) {
      console.log('Fail to pull role data from the server');
      return [];
    }
    const raw = await response.text();
    const roles = JSON.parse(raw).data as IRawRole[];
    return roles.filter(role => !role.assignedUserId) as ILookupItem[];
  } catch (ex) {
    console.log('Fail to pull role data from the server');
    return [];
  }
};

const pullDepartments = async () => {
  const { token } = getNonNullUser();
  const url = getUrl(`/UserManager/Department`, businessContextId);

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(token)
    });
    if (!response.ok) {
      console.log('Fail to pull department data from server');
      return [];
    }
    const raw = await response.text();
    const departments = JSON.parse(raw).data as IDepartment[];
    return departments;
  } catch (ex) {
    console.log('Fail to pull department data from server');

    return [];
  }
};

const pullSetupData = async () => {
  const roles = await pullRoles();
  const departments = await pullDepartments();
  return {
    roles,
    departments
  } as ISetupData;
};
const pullUserList = async () => {
  const { token } = getNonNullUser();
  const url = getUrl(`/UserManager/User`, businessContextId);
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(token)
    });
    if (!response.ok) {
      console.log('Fail to get the user list');
      return [];
    }
    const raw = await response.text();
    const users = JSON.parse(raw).data as IUser[];
    return users;
  } catch (ex) {
    console.log('Fail to get the user list');
    return [];
  }
};

const pushCreateRole = async (role: IPushRole) => {
  const { token } = getNonNullUser();
  const url = getUrl(`/UserManager/Role`, businessContextId);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({ ...role, estimatedLoad: 1 })
    });
    if (!response.ok) {
      console.log('Fail to create a new role');
      return false;
    }
    const raw = await response.text();
    const newRole = JSON.parse(raw).data as ILookupItem;
    return newRole;
  } catch (ex) {
    console.log('Fail to create a new role');
    return false;
  }
};

const attachRoleToGroup = async (roleId: number, userGroupId: number) => {
  const { token } = getNonNullUser();
  const url = getUrl(
    `/UserManager/Role/${roleId}/setGroups`,
    businessContextId
  );
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify([userGroupId])
    });
    if (!response.ok) {
      return false;
    }
    return true;
  } catch (ex) {
    console.log('Fail to create a new role');
    return false;
  }
};
const deleteUser = async (userId: number) => {
  const { token } = getNonNullUser();
  const setRoleUrl = getUrl(
    `/UserManager/User/${userId}/relieveRoles`,
    businessContextId
  );
  try {
    const response = await fetch(setRoleUrl, {
      method: 'PUT',
      headers: getHeaders(token),
      body: JSON.stringify([])
    });
    if (!response.ok) {
      console.log('Fail to archive an user here');
      return false;
    }
    console.log('An user is archived');
    return true;
  } catch (ex) {
    console.log('Fail to archive an user');
    return false;
  }
};

const pushUser = async (user: IPushUser) => {
  const { token } = getNonNullUser();
  const url = getUrl(`/UserManager/User/Create`, businessContextId);
  await attachRoleToGroup(user.role, siteUserGroupId);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: getHeaders(token),
      body: JSON.stringify({
        entity: {
          ...user,
          name: user.username,
          parentId: user.department,
          loginEnabled: true,
          guid: Guid.create().toString(),
          position: ''
        },
        role: user.role,
        weekdays: user.emailNotification,
        dashboard: defaultDashboardId
      })
    });
    if (!response.ok) {
      console.log('Fail to create a new user');
      return false;
    }
    console.log('New user created');
    return true;
  } catch (ex) {
    console.log('Fail to create a new user');
    return false;
  }
};
export {
  pullSetupData,
  pullUserList,
  pushCreateRole,
  deleteUser,
  pushUser,
  pullDepartments
};
